(() => {
  'use strict';

  const STORAGE_KEY = 'techub-pwa-install-dismissed-until';
  const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;
  const root = document.querySelector('[data-techub-pwa]');
  if (!root || document.documentElement.classList.contains('shopify-design-mode')) return;

  const ua = window.navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/i.test(ua);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    window.navigator.standalone === true;
  const platform = isIOS ? 'ios' : (isAndroid ? 'android' : 'other');
  const language = (document.documentElement.lang || '').toLowerCase();
  const isArabic = language.startsWith('ar');
  const locale = isArabic ? 'ar' : 'en';
  const getText = (name) => root.getAttribute(`data-${name}-${locale}`) || '';
  const title = root.querySelector('[data-pwa-title]');
  const copy = root.querySelector('[data-pwa-copy]');
  const steps = root.querySelector('[data-pwa-manual-steps]');
  const installButton = root.querySelector('[data-pwa-install]');
  const dismissButton = root.querySelector('[data-pwa-dismiss]');

  // Local event hook only. Nothing is sent to GA4, GTM, dataLayer or a remote endpoint.
  // A merchant can connect these events to GTM manually when desired.
  const emitManualEvent = (eventName, installMethod) => {
    try {
      window.dispatchEvent(new CustomEvent('techub:pwa-analytics', {
        detail: {
          event: eventName,
          pwa_platform: platform,
          pwa_language: locale,
          install_method: installMethod
        }
      }));
    } catch (_) {}
  };

  if (isStandalone) {
    emitManualEvent('pwa_standalone_open', isIOS ? 'ios_home_screen' : 'standalone');
    try {
      const firstOpenKey = 'techub-pwa-first-standalone-open-v1';
      if (!localStorage.getItem(firstOpenKey)) {
        localStorage.setItem(firstOpenKey, '1');
        emitManualEvent('pwa_standalone_first_open', isIOS ? 'ios_home_screen_estimate' : 'standalone_first_launch');
      }
    } catch (_) {}
    try {
      const sessionOpenKey = 'techub-pwa-standalone-session-open-v1';
      if (!sessionStorage.getItem(sessionOpenKey)) {
        sessionStorage.setItem(sessionOpenKey, '1');
        emitManualEvent('pwa_standalone_session_start', 'standalone');
      }
    } catch (_) {}
    return;
  }
  if (!isIOS && !isAndroid) return;

  if (title) title.textContent = getText('install-title');
  if (copy) copy.textContent = getText('install-copy');
  if (dismissButton) dismissButton.textContent = getText('dismiss');
  if (steps) steps.textContent = getText(isIOS ? 'ios-steps' : 'android-steps');

  const isSnoozed = () => {
    try {
      const until = Number(localStorage.getItem(STORAGE_KEY) || 0);
      return Number.isFinite(until) && until > Date.now();
    } catch (_) {
      return false;
    }
  };

  const setStepsExpanded = (expanded) => {
    if (steps) steps.hidden = !expanded;
    root.classList.toggle('is-steps-expanded', expanded);
    document.body.classList.toggle('techub-pwa-steps-expanded', expanded);
    installButton?.setAttribute('aria-expanded', String(expanded));
  };

  const showPrompt = () => {
    if (isSnoozed()) return;
    root.hidden = false;
    root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('techub-pwa-visible');
    emitManualEvent('pwa_install_banner_view', isIOS ? 'ios_manual' : 'android_manual_or_native');
  };

  const hidePrompt = () => {
    root.hidden = true;
    root.setAttribute('aria-hidden', 'true');
    root.classList.remove('is-steps-expanded');
    document.body.classList.remove('techub-pwa-visible', 'techub-pwa-steps-expanded');
  };

  const snoozePrompt = () => {
    emitManualEvent('pwa_install_dismiss', isIOS ? 'ios_manual' : 'android_manual_or_native');
    try { localStorage.setItem(STORAGE_KEY, String(Date.now() + SNOOZE_MS)); } catch (_) {}
    hidePrompt();
  };

  dismissButton?.addEventListener('click', snoozePrompt);

  if (isIOS) {
    if (installButton) installButton.textContent = getText('ios-button');
    installButton?.addEventListener('click', () => {
      emitManualEvent('pwa_install_click', 'ios_manual_add_to_home_screen');
      const expanded = Boolean(steps?.hidden);
      setStepsExpanded(expanded);
      if (expanded) emitManualEvent('pwa_install_instructions_opened', 'ios_manual_add_to_home_screen');
    });
      // Show as soon as this deferred, bottom-of-page script runs; do not wait for window.load.
    showPrompt();
    return;
  }

  let deferredPrompt = null;
  const setAndroidButtonMode = (nativePromptAvailable) => {
    if (installButton) installButton.textContent = nativePromptAvailable ? getText('install-button') : getText('android-guide-button');
    if (nativePromptAvailable && steps) setStepsExpanded(false);
  };
  setAndroidButtonMode(false);

  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredPrompt = event;
    setAndroidButtonMode(true);
  });

  installButton?.addEventListener('click', async () => {
    if (!deferredPrompt) {
      emitManualEvent('pwa_install_click', 'android_manual_add_to_home_screen');
      const expanded = Boolean(steps?.hidden);
      setStepsExpanded(expanded);
      if (expanded) emitManualEvent('pwa_install_instructions_opened', 'android_manual_add_to_home_screen');
      return;
    }

    emitManualEvent('pwa_install_click', 'android_browser_prompt');
    installButton.disabled = true;
    try {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice && choice.outcome === 'accepted') {
        emitManualEvent('pwa_install_accepted', 'android_browser_prompt');
        hidePrompt();
      } else {
        emitManualEvent('pwa_install_declined', 'android_browser_prompt');
        try { localStorage.setItem(STORAGE_KEY, String(Date.now() + SNOOZE_MS)); } catch (_) {}
        hidePrompt();
      }
    } catch (_) {
      // Keep the prompt visible and expose manual browser instructions if native prompt fails.
      deferredPrompt = null;
      setAndroidButtonMode(false);
      setStepsExpanded(true);
    } finally {
      deferredPrompt = null;
      if (installButton) installButton.disabled = false;
    }
  });

  window.addEventListener('appinstalled', () => {
    emitManualEvent('pwa_installed', 'android_browser_prompt');
    hidePrompt();
  });

  // Show immediately. Native install support upgrades the button whenever the browser event arrives.
  showPrompt();
})();
